import { useEffect, useState } from "react";
import { Check, Copy, Inbox, RotateCcw, Search, Zap } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND } from "@/lib/api";


function TunnelStatus({ appId }) {
    const [active, setActive] = useState(false);
    useEffect(() => {
        let dead = false;
        const check = async () => {
            try {
                const r = await fetch(`${API}/tunnel/${appId}/status`, { credentials: "include" });
                if (r.ok && !dead) setActive((await r.json()).active);
            } catch {
                /* offline */
            }
        };
        check();
        const t = setInterval(check, 15000);
        return () => {
            dead = true;
            clearInterval(t);
        };
    }, [appId]);
    return (
        <span
            data-testid="receiver-tunnel-status"
            className={`flex items-center gap-1.5 font-mono text-[10px] ${
                active ? "text-emerald-400" : "text-slate-600"
            }`}
        >
            <span
                className={`h-1.5 w-1.5 rounded-full ${
                    active ? "animate-pulse bg-emerald-400" : "bg-slate-700"
                }`}
            />
            {active ? "tunnel connected" : "no tunnel"}
        </span>
    );
}


export default function ReceiverPanel({ appId, app, hooks: wsHooks, onConfigSaved }) {    const [hooks, setHooks] = useState([]);
    const hydrate = (cfg) => {
        const list = [...(cfg?.forward_targets || [])].map((t) => ({
            url: t.url || "",
            // templates are stored as parsed JSON — the textarea needs a string
            template:
                t.template && typeof t.template === "object"
                    ? JSON.stringify(t.template, null, 2)
                    : t.template || "",
        }));
        if (cfg?.forward_url && !list.some((t) => t.url === cfg.forward_url))
            list.unshift({ url: cfg.forward_url, template: "" });
        return list.length ? list : [{ url: "", template: "" }];
    };
    const [targets, setTargets] = useState(() => hydrate(app?.config));
    const [openId, setOpenId] = useState(null);
    const [copied, setCopied] = useState(false);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState(null); // non-null while a server search is active
    const [previews, setPreviews] = useState({}); // {rowIndex: {loading, text, error}}

    const testTemplate = async (i) => {
        const tpl = (targets[i].template || "").trim();
        if (!tpl) return;
        let parsed;
        try {
            parsed = JSON.parse(tpl);
        } catch {
            toast.error("Template is not valid JSON", { id: "routing-save-error" });
            return;
        }
        setPreviews((p) => ({ ...p, [i]: { loading: true } }));
        try {
            const r = await fetch(`${API}/apps/${appId}/transform-preview`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ template: parsed }),
            });
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.detail || "failed");
            setPreviews((p) => ({
                ...p,
                [i]: { text: JSON.stringify(d.rendered, null, 2), hook: d.hook_id },
            }));
        } catch (e) {
            setPreviews((p) => ({ ...p, [i]: { error: String(e.message) } }));
        }
    };

    // re-hydrate when the app prop refreshes (e.g. after save or WS update)
    const appConfig = app?.config;
    useEffect(() => {
        setTargets(hydrate(appConfig));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appConfig]);

    useEffect(() => {
        let dead = false;
        (async () => {
            try {
                const r = await fetch(`${API}/apps/${appId}/hooks`, { credentials: "include" });
                if (r.ok) {
                    const d = await r.json();
                    if (!dead) setHooks(d.hooks || []);
                }
            } catch {
                /* best-effort */
            }
        })();
        return () => {
            dead = true;
        };
    }, [appId]);

    useEffect(() => {
        if (!wsHooks?.length) return;
        setHooks((prev) => {
            const seen = new Set(prev.map((h) => h.id));
            return [...wsHooks.filter((h) => !seen.has(h.id)), ...prev].slice(0, 80);
        });
    }, [wsHooks]);

    const hookUrl = `${BACKEND}/api/hooks/${app?.hook_slug || "…"}`;

    // server-side search across all stored webhook bodies (debounced)
    useEffect(() => {
        const q = query.trim();
        if (!q) {
            setResults(null);
            return undefined;
        }
        const t = setTimeout(async () => {
            try {
                const r = await fetch(
                    `${API}/apps/${appId}/hooks?q=${encodeURIComponent(q)}`,
                    { credentials: "include" },
                );
                if (r.ok) setResults((await r.json()).hooks || []);
            } catch {
                /* keep previous results */
            }
        }, 350);
        return () => clearTimeout(t);
    }, [query, appId]);

    const shownHooks = results !== null ? results : hooks;

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(hookUrl);
            setCopied(true);
            toast.success("Webhook URL copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    const saveForward = async () => {
        setSaving(true);
        try {
            const cleaned = targets
                .filter((t) => t.url.trim())
                .slice(0, 5)
                .map((t) => {
                    const row = { url: t.url.trim() };
                    const tpl = (t.template || "").trim();
                    if (tpl) row.template = JSON.parse(tpl); // throws on bad JSON
                    return row;
                });
            const r = await fetch(`${API}/apps/${appId}/config`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    config: { forward_targets: cleaned, forward_url: "" },
                }),
            });
            if (!r.ok) throw new Error("failed");
            toast.success(
                cleaned.length
                    ? `Routing saved — ${cleaned.length} destination${cleaned.length > 1 ? "s" : ""}`
                    : "Routing cleared",
            );
            onConfigSaved?.();
        } catch (e) {
            toast.error(
                e instanceof SyntaxError
                    ? "A transform template is not valid JSON"
                    : "Could not save",
                { id: "routing-save-error" }, // dedupe repeat clicks
            );
        } finally {
            setSaving(false);
        }
    };

    const setTarget = (i, patch) =>
        setTargets((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

    const replay = async (hookId) => {
        try {
            const r = await fetch(`${API}/apps/${appId}/hooks/${hookId}/replay`, {
                method: "POST",
                credentials: "include",
            });
            if (!r.ok) throw new Error("failed");
            toast.success("Webhook replayed");
        } catch {
            toast.error("Replay failed");
        }
    };

    const pretty = (body) => {
        try {
            return JSON.stringify(JSON.parse(body), null, 2);
        } catch {
            return body;
        }
    };

    return (
        <div data-testid="receiver-panel" className="glass-panel rounded-xl p-5">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                <Inbox className="h-4 w-4 text-cyan-400" />
                Inbound webhook
            </h3>

            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-800/80 bg-slate-950/60 px-4 py-3">
                <span className="rounded border border-emerald-500/30 bg-emerald-950/50 px-2 py-0.5 font-mono text-[10px] font-bold text-emerald-300">
                    POST
                </span>
                <code
                    data-testid="receiver-url"
                    className="min-w-0 flex-1 truncate font-mono text-[11px] text-cyan-300"
                >
                    {hookUrl}
                </code>
                <button
                    data-testid="receiver-copy-button"
                    onClick={copyUrl}
                    className="flex shrink-0 items-center gap-1.5 rounded border border-slate-700 px-2.5 py-1.5 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                >
                    {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                    {copied ? "copied" : "copy"}
                </button>
            </div>

            <div className="mt-3 space-y-2.5">
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    route inbound webhooks to
                </span>
                {targets.map((t, i) => (
                    <div
                        key={i}
                        data-testid={`receiver-target-${i}`}
                        className="rounded-lg border border-slate-800/80 bg-slate-950/50 p-3"
                    >
                        <div className="flex items-center gap-2">
                            <input
                                data-testid={`receiver-target-url-${i}`}
                                value={t.url}
                                onChange={(e) => setTarget(i, { url: e.target.value })}
                                placeholder="https://api.yourservice.com/incoming"
                                className="min-w-0 flex-1 rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2 font-mono text-[11px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                            />
                            <button
                                data-testid={`receiver-target-remove-${i}`}
                                onClick={() => setTargets((rows) => rows.filter((_, j) => j !== i))}
                                className="shrink-0 rounded border border-slate-700 px-2 py-2 font-mono text-[10px] text-slate-500 transition-colors hover:border-rose-500/50 hover:text-rose-300"
                                aria-label="Remove destination"
                            >
                                ×
                            </button>
                        </div>
                        <textarea
                            data-testid={`receiver-target-template-${i}`}
                            value={t.template || ""}
                            onChange={(e) => setTarget(i, { template: e.target.value })}
                            placeholder='Optional transform (JSON): {"text": "Order {{data.order_id}} paid", "raw": "{{$raw}}"}'
                            rows={2}
                            className="mt-2 w-full rounded-md border border-slate-800/70 bg-slate-950/60 px-3 py-2 font-mono text-[10.5px] text-slate-300 placeholder:text-slate-700 focus:border-cyan-500/60 focus:outline-none"
                        />
                        {(t.template || "").trim() && (
                            <div className="mt-2">
                                <button
                                    data-testid={`receiver-target-test-${i}`}
                                    onClick={() => testTemplate(i)}
                                    disabled={previews[i]?.loading}
                                    className="flex items-center gap-1.5 rounded border border-slate-700 px-2.5 py-1 font-mono text-[10px] text-slate-400 transition-colors hover:border-cyan-500/50 hover:text-cyan-300 disabled:opacity-50"
                                >
                                    <Zap className="h-3 w-3" />
                                    {previews[i]?.loading ? "testing…" : "test against last webhook"}
                                </button>
                                {previews[i]?.text && (
                                    <pre
                                        data-testid={`receiver-target-preview-${i}`}
                                        className="mt-2 max-h-36 overflow-auto rounded-md border border-emerald-500/25 bg-emerald-950/10 p-2.5 font-mono text-[10.5px] leading-relaxed text-emerald-200"
                                    >
                                        {previews[i].text}
                                    </pre>
                                )}
                                {previews[i]?.error && (
                                    <p
                                        data-testid={`receiver-target-preview-error-${i}`}
                                        className="mt-2 rounded-md border border-rose-500/30 bg-rose-950/20 px-2.5 py-1.5 font-mono text-[10px] text-rose-300"
                                    >
                                        {previews[i].error}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                ))}
                <div className="flex items-center gap-2">
                    {targets.length < 5 && (
                        <button
                            data-testid="receiver-add-target"
                            onClick={() => setTargets((rows) => [...rows, { url: "", template: "" }])}
                            className="rounded-md border border-slate-700 px-3.5 py-2 font-mono text-[11px] text-slate-400 transition-colors hover:border-cyan-500/50 hover:text-cyan-300"
                        >
                            + Add destination
                        </button>
                    )}
                    <button
                        data-testid="receiver-save-forward"
                        onClick={saveForward}
                        disabled={saving}
                        className="rounded-md border border-cyan-500/40 bg-cyan-950/40 px-3.5 py-2 font-mono text-[11px] font-semibold text-cyan-300 transition-colors hover:bg-cyan-950/70 disabled:opacity-50"
                    >
                        {saving ? "Saving…" : "Save routing"}
                    </button>
                    <span className="font-mono text-[9px] text-slate-600">
                        {"{{field.path}}"} fills from each payload · {"{{$raw}}"} = untouched body
                    </span>
                </div>

                <div className="mt-4 rounded-lg border border-slate-800/80 bg-slate-950/40 p-3.5">
                    <div className="flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                            dev tunnel — public URL → localhost
                        </span>
                        <TunnelStatus appId={appId} />
                    </div>
                    <pre
                        data-testid="receiver-tunnel-command"
                        className="mt-2.5 overflow-x-auto rounded-md border border-slate-800/70 bg-slate-950/70 p-3 font-mono text-[10.5px] leading-relaxed text-slate-300"
                    >{`curl -o mdh_tunnel.mjs ${BACKEND}/api/agent/tunnel-script
MDH_TOKEN=mdh_pat_… node mdh_tunnel.mjs ${appId} --to http://localhost:8080`}</pre>
                    <p className="mt-2 font-mono text-[9px] leading-relaxed text-slate-600">
                        While the tunnel runs, your local handler's response goes back to the
                        producer — real end-to-end testing against live Stripe/GitHub deliveries.
                    </p>
                </div>
            </div>

            <div className="mt-4 space-y-1.5">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-600" />
                    <input
                        data-testid="hook-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search every stored webhook body…"
                        className="w-full rounded-md border border-slate-800 bg-slate-950/70 py-1.5 pl-7 pr-3 font-mono text-[10px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                    />
                    {results !== null && (
                        <span
                            data-testid="hook-search-count"
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 font-mono text-[9px] text-cyan-400"
                        >
                            {results.length} hit{results.length === 1 ? "" : "s"}
                        </span>
                    )}
                </div>
                {shownHooks.length === 0 && (
                    <div className="flex flex-col items-center gap-2.5 rounded-lg border border-dashed border-slate-800 py-10 text-center">
                        {results !== null ? (
                            <p className="font-mono text-[11px] text-slate-600">
                                No stored webhook matches "{query.trim()}".
                            </p>
                        ) : (
                            <>
                                <Inbox className="h-6 w-6 text-slate-700" />
                                <p className="font-mono text-xs font-semibold text-slate-400">No webhooks yet</p>
                                <p className="max-w-sm font-mono text-[11px] leading-relaxed text-slate-600">
                                    Send a POST to your webhook URL above and it lands here — headers, payload, signature check and all.
                                </p>
                            </>
                        )}
                    </div>
                )}
                {shownHooks.map((h) => (
                    <div
                        key={h.id}
                        data-testid={`hook-row-${h.id}`}
                        className="rounded-lg border border-slate-800/80 bg-slate-950/50"
                    >
                        <button
                            data-testid={`hook-expand-${h.id}`}
                            onClick={() => setOpenId(openId === h.id ? null : h.id)}
                            className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-left font-mono text-[11px]"
                        >
                            <span className="flex items-center gap-2.5">
                                <span className="text-slate-600">
                                    {(h.received_at || "").slice(11, 19)}
                                </span>
                                <span className="text-slate-300">{h.method || "POST"}</span>
                                <span className="hidden max-w-[220px] truncate text-slate-500 sm:inline">
                                    {h.content_type || "payload"}
                                </span>
                            </span>
                            <span className="flex items-center gap-2">
                                {h.forward_status != null && (
                                    <span
                                        className={`rounded border px-1.5 py-px text-[9px] ${
                                            h.forward_status < 400
                                                ? "border-emerald-500/30 bg-emerald-950/40 text-emerald-300"
                                                : "border-rose-500/30 bg-rose-950/40 text-rose-300"
                                        }`}
                                    >
                                        fwd {h.forward_status}
                                    </span>
                                )}
                                {h.replayed && (
                                    <span className="rounded border border-slate-700 px-1.5 py-px text-[9px] text-slate-500">
                                        replayed
                                    </span>
                                )}
                                <span className="text-slate-600">
                                    {(h.body || "").length > 1024
                                        ? `${((h.body || "").length / 1024).toFixed(1)}kb`
                                        : `${(h.body || "").length}b`}
                                </span>
                            </span>
                        </button>
                        {openId === h.id && (
                            <div className="border-t border-slate-800/60 px-4 py-3">
                                <pre className="max-h-56 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-cyan-100">
                                    {pretty(h.body || "")}
                                </pre>
                                <button
                                    data-testid={`hook-replay-${h.id}`}
                                    onClick={() => replay(h.id)}
                                    className="mt-3 flex items-center gap-1.5 rounded border border-amber-500/40 bg-amber-950/40 px-3 py-1.5 font-mono text-[10px] font-semibold text-amber-300 transition-colors hover:bg-amber-950/70"
                                >
                                    <RotateCcw className="h-3 w-3" /> Replay
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
